// تب «نسخه، درخواست و ارجاع» صفحه مراجعه‌کننده: SCR-STAFF-700 (نسخه‌ها: وضعیت، چاپ، لینک امن، ابطال)، SCR-STAFF-702 (درخواست‌ها با وضعیت
// و دیرکرد، نتایج با پرچم و بازبینی؛ ثبت نتیجه بدون درخواست) و ارجاع‌های مراجعه‌کننده. پذیرش فهرست عملیاتی بدون محتوای بالینی می‌بیند (C-AGENT-871).
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type MedOrderView,
  type MedPrescriptionListItem,
  type MedReferralListItem,
  type MedResultView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ReasonDialog, toJalaliDateTime, useDisplaySettings, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { medKeys, OrderStatusChip, ReferralStatusChip, RxStatusChip, saveBlob, SCR_STAFF_700, SCR_STAFF_702, SCR_STAFF_705 } from './common';
import { InsurerPatientFlagsPanel } from './SupplementaryPanels';
import { NationalSubmissionPanel } from './national';
import { printOrder } from './OrderEditor';
import { ExternalReferralDialog, InboundReferralsPanel, ResultTrendPanel } from './Phase2Panels';
import { InsurerOpenReferralsPanel, InsurerReferralTrackingPanel } from './InsurerReferralPanels';
import { EnterResultDialog, ResultReviewDialog } from './ResultDialogs';

type Section = 'prescriptions' | 'orders' | 'referrals';

export function PatientMedicalOrdersPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const { timeZone } = useDisplaySettings();
  const tenantId = session?.tenantId;
  const full = access.accessMode === 'Full';
  // نمای بالینی (view) یا عملیاتی پذیرش بدون محتوای بالینی (print/track — C-AGENT-1050)؛ سرور جزئیات را تفکیک می‌کند.
  const canPrescriptions = access.can('med.prescription.view') || access.can('med.prescription.print');
  const canOrders = access.can('med.order.view') || access.can('med.order.track');
  const [section, setSection] = useState<Section>(canPrescriptions ? 'prescriptions' : canOrders ? 'orders' : 'referrals');

  const [voiding, setVoiding] = useState<string | null>(null);
  const [entering, setEntering] = useState<MedOrderView | null | undefined>(undefined);
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const sendKey = useIdempotencyKey();
  const [trend, setTrend] = useState(false);
  const [external, setExternal] = useState<string | null>(null);
  // AP-CLN-04: پذیرش (med.referral.schedule) ارجاع‌های ورودی را می‌بیند و ثبت می‌کند (US-MED-064).
  const canReferrals = access.can('med.referral.respond') || access.can('med.referral.schedule');
  const path = { params: { path: { patientId } } };
  const time = (value: string) => toJalaliDateTime(value, { timeZone });

  const prescriptions = useQuery({
    queryKey: medKeys.patient(tenantId, patientId, 'prescriptions'),
    enabled: section === 'prescriptions' && canPrescriptions,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/patients/{patientId}/prescriptions', path)),
  });
  const orders = useQuery({
    queryKey: medKeys.patient(tenantId, patientId, 'orders'),
    enabled: section === 'orders' && canOrders,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/patients/{patientId}/orders', path)),
  });
  const results = useQuery({
    queryKey: medKeys.patient(tenantId, patientId, 'results'),
    enabled: section === 'orders' && canOrders,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/patients/{patientId}/results', path)),
  });
  const referrals = useQuery({
    queryKey: medKeys.patient(tenantId, patientId, 'referrals'),
    enabled: section === 'referrals' && access.can('med.referral.respond'),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/patients/{patientId}/referrals', path)),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['medorders', tenantId, 'patient', patientId] });

  const share = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/medical-orders/prescriptions/{id}:share', { params: { path: { id } } })),
    onSuccess: () => setNotice(t('med:rx.shared')),
  });
  const voidRx = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => unwrap(tenantApi.POST('/api/v1/medical-orders/prescriptions/{id}:void', { params: { path: { id } }, body: { reason } })),
    onSuccess: () => {
      setVoiding(null);
      refresh();
    },
  });
  const printRx = async (row: MedPrescriptionListItem) => {
    const response = await tenantApi.GET('/api/v1/medical-orders/prescriptions/{id}/print', { params: { path: { id: row.id }, query: { format: 'Pdf' } }, parseAs: 'blob' });
    if (response.response.ok && response.data) {
      saveBlob(response.data as unknown as Blob, `${row.number ?? 'rx'}.pdf`);
    }
  };

  const rxColumns: DataTableColumn<MedPrescriptionListItem>[] = [
    { id: 'number', headerKey: 'med:rx.number', cell: (row) => <bdi dir="ltr">{row.number ?? '—'}</bdi> },
    { id: 'status', headerKey: 'med:common.status', cell: (row) => <RxStatusChip status={row.status} /> },
    { id: 'date', headerKey: 'med:common.date', cell: (row) => time(row.issuedAt ?? row.draftedAt) },
    { id: 'prescriber', headerKey: 'med:rx.prescriber', cell: (row) => row.prescriberName ?? '—' },
    { id: 'items', headerKey: 'med:rx.items', cell: (row) => (row.itemNames.length ? row.itemNames.join(t('med:common.list_separator')) : '—') },
    { id: 'controlled', headerKey: 'med:rx.controlled', cell: (row) => (row.isControlled ? <Chip size="small" color="secondary" label={t('med:rx.controlled')} /> : null) },
    {
      id: 'actions',
      headerKey: 'med:common.actions',
      cell: (row) =>
        row.status === 'Issued' ? (
          <Stack direction="row" spacing={0.5}>
            {access.can('med.prescription.print') && (
              <Button size="small" onClick={() => void printRx(row)} data-testid="med-rx-row-print">
                {t('med:rx.print')}
              </Button>
            )}
            {!row.isControlled && access.can('med.prescription.print') && (
              <Button size="small" onClick={() => share.mutate(row.id)} data-testid="med-rx-row-share">
                {t('med:rx.share')}
              </Button>
            )}
            {full && access.can('med.prescription.void') && (
              <Button size="small" color="error" onClick={() => setVoiding(row.id)} data-testid="med-rx-row-void">
                {t('med:rx.void')}
              </Button>
            )}
          </Stack>
        ) : null,
    },
  ];
  // AC-MED-043.1: ارسال الکترونیک درخواست به مرکز طرف قرارداد. پذیرش ← وضعیت Sent با شماره پذیرش؛ ناکامی ← وضعیت
  // دست‌نخورده با علت، تا کاربر برگه را چاپ کند یا دوباره بفرستد (هیچ درخواست نیم‌کاره‌ای ساخته نمی‌شود).
  const sendElectronic = useMutation({
    mutationFn: (order: MedOrderView) =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/orders/{id}:send-electronic', {
          params: { path: { id: order.id } },
          headers: { 'Idempotency-Key': sendKey.current() },
        }),
      ),
    onSettled: (_data, error) => sendKey.settle(error),
    onSuccess: (view) => {
      setNotice(
        view.accepted
          ? t('med:order.sent_accepted', { accession: view.accessionNumber ?? '' })
          : t('med:order.sent_rejected', { reason: view.providerMessage ?? view.errorReason ?? '' }),
      );
      void queryClient.invalidateQueries({ queryKey: medKeys.patient(tenantId, patientId, 'orders') });
    },
  });

  const orderColumns: DataTableColumn<MedOrderView>[] = [
    { id: 'number', headerKey: 'med:order.number', cell: (row) => <bdi dir="ltr">{row.number}</bdi> },
    { id: 'type', headerKey: 'med:order.type', cell: (row) => t(`med:order_type.${row.type}`) },
    { id: 'items', headerKey: 'med:order.items', cell: (row) => row.items.map((item) => item.name).join(t('med:common.list_separator')) },
    { id: 'priority', headerKey: 'med:order.priority', cell: (row) => (row.priority === 'Urgent' ? <Chip size="small" color="warning" label={t('med:priority.Urgent')} /> : t('med:priority.Routine')) },
    { id: 'status', headerKey: 'med:common.status', cell: (row) => <Stack direction="row" spacing={0.5}><OrderStatusChip status={row.status} />{row.overdue ? <Chip size="small" color="error" label={t('med:order.overdue')} data-testid="med-order-overdue" /> : null}</Stack> },
    { id: 'date', headerKey: 'med:common.date', cell: (row) => time(row.orderedAt) },
    {
      id: 'actions',
      headerKey: 'med:common.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Button size="small" onClick={() => void printOrder(tenantApi, row)} data-testid="med-order-row-print">
            {t('med:order.print')}
          </Button>
          {row.partnerId && !row.internal && row.status === 'Ordered' && access.can('med.order.create') && (
            <Button
              size="small"
              onClick={() => sendElectronic.mutate(row)}
              disabled={sendElectronic.isPending}
              data-testid={`med-order-row-send-${row.id}`}
            >
              {t('med:order.send_electronic')}
            </Button>
          )}
          {full && access.can('med.result.enter') && row.status !== 'Cancelled' && row.status !== 'Reviewed' && row.status !== 'Resulted' && (
            <Button size="small" onClick={() => setEntering(row)} data-testid="med-order-row-result">
              {t('med:result.enter')}
            </Button>
          )}
        </Stack>
      ),
    },
  ];
  const resultColumns: DataTableColumn<MedResultView>[] = [
    { id: 'title', headerKey: 'med:result.title_field', cell: (row) => row.title ?? row.orderNumber ?? t(`med:order_type.${row.type}`) },
    { id: 'received', headerKey: 'med:common.date', cell: (row) => time(row.receivedAt) },
    {
      id: 'state',
      headerKey: 'med:common.status',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Chip size="small" variant="outlined" label={t(`med:result_status.${row.status}`)} />
          {row.hasCritical ? <Chip size="small" color="error" label={t('med:result.critical_short')} /> : row.hasAbnormal ? <Chip size="small" color="warning" label={t('med:result.abnormal')} /> : null}
        </Stack>
      ),
    },
    {
      id: 'actions',
      headerKey: 'med:common.actions',
      cell: (row) =>
        access.can('cln.record.view') ? (
          <Button size="small" onClick={() => setReviewing(row.id)} data-testid="med-result-row-open">
            {row.status === 'Received' && access.can('med.result.review') ? t('med:result.review') : t('med:common.view')}
          </Button>
        ) : null,
    },
  ];
  const referralColumns: DataTableColumn<MedReferralListItem>[] = [
    { id: 'from', headerKey: 'med:referral.from', cell: (row) => row.fromProviderName ?? '—' },
    { id: 'to', headerKey: 'med:referral.to', cell: (row) => row.toProviderName ?? row.toSpecialty ?? row.externalName ?? t(`med:target_kind.${row.targetKind}`) },
    { id: 'urgency', headerKey: 'med:referral.urgency', cell: (row) => t(`med:priority.${row.urgency}`) },
    { id: 'status', headerKey: 'med:common.status', cell: (row) => <ReferralStatusChip status={row.status} /> },
    { id: 'date', headerKey: 'med:common.date', cell: (row) => time(row.createdAt) },
    {
      id: 'actions',
      headerKey: 'med:common.actions',
      cell: (row) =>
        row.targetKind === 'External' && (access.can('med.referral.create') || access.can('med.referral.schedule')) ? (
          <Button size="small" onClick={() => setExternal(row.id)} data-testid="med-referral-external-open">
            {t('med:referral.letter_and_response')}
          </Button>
        ) : null,
    },
  ];

  return (
    <Stack spacing={1.5} data-testid="med-patient-panel">
      <Tabs value={section} onChange={(_, value: Section) => setSection(value)} aria-label={t('med:patient.title')}>
        {canPrescriptions && <Tab value="prescriptions" label={t('med:patient.prescriptions')} data-testid="med-tab-prescriptions" />}
        {canOrders && <Tab value="orders" label={t('med:patient.orders')} data-testid="med-tab-orders" />}
        {canReferrals && <Tab value="referrals" label={t('med:patient.referrals')} data-testid="med-tab-referrals" />}
      </Tabs>
      {notice && (
        <Chip color="success" label={notice} onDelete={() => setNotice(null)} data-testid="med-patient-notice" />
      )}
      {section === 'prescriptions' && (
        <Stack spacing={2} data-screen-id={SCR_STAFF_700}>
          <DataTable captionKey="med:patient.prescriptions" columns={rxColumns} rows={prescriptions.data?.items} getRowId={(row) => row.id} isLoading={prescriptions.isPending} error={prescriptions.error} onRetry={() => void prescriptions.refetch()} emptyTitleKey="med:patient.no_prescriptions" />
          {/* AP-MED-02 (SCR-STAFF-700): وضعیت ملی مراجعه‌کننده — کد رهگیری، استعلام وضعیت و حذف در مهلت. */}
          <NationalSubmissionPanel patientId={patientId} canSubmit={access.can('med.prescription.national_submit')} />
          {/* AP-MED-05 (SCR-STAFF-700؛ US-MED-097): نشان‌های بیمه‌گر — نام بیماری Clinical است و هر مشاهده ثبت می‌شود. */}
          <InsurerPatientFlagsPanel patientId={patientId} />
        </Stack>
      )}
      {section === 'orders' && (
        <Stack spacing={1.5} data-screen-id={SCR_STAFF_702}>
          <DataTable captionKey="med:patient.orders" columns={orderColumns} rows={orders.data?.items} getRowId={(row) => row.id} isLoading={orders.isPending} error={orders.error} onRetry={() => void orders.refetch()} emptyTitleKey="med:patient.no_orders" />
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <Typography variant="subtitle1" component="h3" sx={{ flexGrow: 1 }}>
              {t('med:patient.results')}
            </Typography>
            {access.can('med.order.view') && (
              <Button onClick={() => setTrend((current) => !current)} data-testid="med-result-trend-toggle">
                {t('med:trend.title')}
              </Button>
            )}
            {full && access.can('med.result.enter') && (
              <Button onClick={() => setEntering(null)} data-testid="med-result-enter-free">
                {t('med:result.enter_without_order')}
              </Button>
            )}
          </Stack>
          {trend && <ResultTrendPanel patientId={patientId} />}
          <DataTable captionKey="med:patient.results" columns={resultColumns} rows={results.data?.items} getRowId={(row) => row.id} isLoading={results.isPending} error={results.error} onRetry={() => void results.refetch()} emptyTitleKey="med:patient.no_results" />
        </Stack>
      )}
      {section === 'referrals' && (
        <Stack spacing={1.5} data-screen-id={SCR_STAFF_705}>
          {access.can('med.referral.respond') && (
            <DataTable captionKey="med:patient.referrals" columns={referralColumns} rows={referrals.data?.items} getRowId={(row) => row.id} isLoading={referrals.isPending} error={referrals.error} onRetry={() => void referrals.refetch()} emptyTitleKey="med:patient.no_referrals" />
          )}
          {access.can('med.referral.schedule') && <InboundReferralsPanel patientId={patientId} />}
          {/* AP-MED-03 (US-MED-099، 100): ارجاع‌های باز بیمار نزد تامین و پیگیری با کد رهگیری — هر دو با
              نشست بیمه‌گر خودِ کاربر؛ نشست همکار به کار نمی‌آید (BR-MED-070). */}
          <InsurerOpenReferralsPanel patientId={patientId} />
          <InsurerReferralTrackingPanel patientId={patientId} />
        </Stack>
      )}
      <ReasonDialog
        open={voiding !== null}
        titleKey="med:rx.void"
        messageKey="med:rx.void_message"
        busy={voidRx.isPending}
        error={voidRx.error}
        onConfirm={(reason) => voiding && voidRx.mutate({ id: voiding, reason })}
        onCancel={() => setVoiding(null)}
      />
      {entering !== undefined && (
        <EnterResultDialog
          open
          patientId={patientId}
          order={entering}
          onClose={() => setEntering(undefined)}
          onSaved={() => {
            setEntering(undefined);
            refresh();
          }}
        />
      )}
      {external && <ExternalReferralDialog open referralId={external} onClose={() => setExternal(null)} />}
      {reviewing && <ResultReviewDialog open resultId={reviewing} onClose={() => setReviewing(null)} onReviewed={() => setReviewing(null)} />}
    </Stack>
  );
}
